import axios from 'axios';
import { retryRequest, retryConfigs, RetryConfig } from '../utils/retry';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://usdc-api.defiportal.org/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 从 localStorage 获取 token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 添加请求时间戳，避免缓存
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      };
    }
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器 - 添加自动重试机制（模仿axios-retry）
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.error('Request error:', error);
    
    const originalRequest = error.config;
    
    // 如果这个请求已经重试过，直接返回错误
    if (originalRequest._retry) {
      return Promise.reject(error);
    }
    
    // 检查是否需要重试（修改后的逻辑）
    const shouldRetry = (() => {
      // POST请求不重试（防止重复提交）
      if (originalRequest.method === 'post' || originalRequest.method === 'POST') {
        return false;
      }
      
      // GET请求的404错误不重试（资源不存在）
      if (originalRequest.method === 'get' || originalRequest.method === 'GET') {
        if (error.response && error.response.status === 404) {
          return false;
        }
      }
      
      // 网络错误
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        return true;
      }
      
      // 请求超时
      if (error.code === 'ECONNABORTED') {
        return true;
      }
      
      // 服务器错误（5xx）
      if (error.response && error.response.status >= 500) {
        return true;
      }
      
      // 408请求超时
      if (error.response && error.response.status === 408) {
        return true;
      }
      
      // 429请求过于频繁
      if (error.response && error.response.status === 429) {
        return true;
      }
      
      // 502 Bad Gateway
      if (error.response && error.response.status === 502) {
        return true;
      }
      
      // 503 Service Unavailable
      if (error.response && error.response.status === 503) {
        return true;
      }
      
      // 504 Gateway Timeout
      if (error.response && error.response.status === 504) {
        return true;
      }
      
      return false;
    })();
    
    // 如果不需要重试，直接返回错误
    if (!shouldRetry) {
      return Promise.reject(error);
    }
    
    // 标记这个请求已经重试过
    originalRequest._retry = true;
    
    // 根据错误类型设置不同的重试配置
    const getRetryConfig = (): RetryConfig => {
      // 429错误使用较长的延迟
      if (error.response && error.response.status === 429) {
        return {
          maxRetries: 3,
          retryDelay: 3000,
          backoffMultiplier: 2,
          retryCondition: (err: any) => {
            return err.response && err.response.status === 429;
          },
          onRetry: (attempt: number, err: any) => {
            console.warn(`请求过于频繁，第${attempt}次重试:`, err.message);
          }
        };
      }
      
      // 网络错误和超时使用标准重试
      if (error.message === 'Network Error' || error.code === 'ECONNABORTED') {
        return {
          maxRetries: 3,
          retryDelay: 1000,
          backoffMultiplier: 2,
          retryCondition: (err: any) => {
            return (
              err.message === 'Network Error' ||
              err.code === 'ECONNABORTED' ||
              err.code === 'ERR_NETWORK'
            );
          },
          onRetry: (attempt: number, err: any) => {
            console.warn(`网络错误，第${attempt}次重试:`, err.message);
          }
        };
      }
      
      // 服务器错误使用保守重试
      if (error.response && error.response.status >= 500) {
        return {
          maxRetries: 3,
          retryDelay: 2000,
          backoffMultiplier: 2,
          retryCondition: (err: any) => {
            return err.response && err.response.status >= 500;
          },
          onRetry: (attempt: number, err: any) => {
            console.warn(`服务器错误，第${attempt}次重试:`, err.message);
          }
        };
      }
      
      // 默认重试配置
      return {
        maxRetries: 3,
        retryDelay: 1000,
        backoffMultiplier: 2,
        retryCondition: (err: any) => {
          return (
            err.message === 'Network Error' ||
            err.code === 'ECONNABORTED' ||
            err.code === 'ERR_NETWORK' ||
            (err.response && err.response.status >= 500) ||
            (err.response && err.response.status === 408) ||
            (err.response && err.response.status === 429) ||
            (err.response && err.response.status === 502) ||
            (err.response && err.response.status === 503) ||
            (err.response && err.response.status === 504)
          );
        },
        onRetry: (attempt: number, err: any) => {
          console.warn(`请求失败，第${attempt}次重试:`, err.message);
        }
      };
    };
    
    // 使用重试机制重新发起请求
    try {
      const retryConfig = getRetryConfig();
      const result = await retryRequest(
        () => api(originalRequest),
        retryConfig
      );
      
      return result;
    } catch (retryError) {
      // 重试失败，返回原始错误
      return Promise.reject(error);
    }
  }
);

// 带重试的API方法
export const apiWithRetry = {
  // GET 请求
  get: async (url: string, config?: any, retryConfig?: RetryConfig) => {
    return retryRequest(
      () => api.get(url, config),
      retryConfig || retryConfigs.standard
    );
  },

  // POST 请求
  post: async (url: string, data?: any, config?: any, retryConfig?: RetryConfig) => {
    return retryRequest(
      () => api.post(url, data, config),
      retryConfig || retryConfigs.standard
    );
  },

  // PUT 请求
  put: async (url: string, data?: any, config?: any, retryConfig?: RetryConfig) => {
    return retryRequest(
      () => api.put(url, data, config),
      retryConfig || retryConfigs.standard
    );
  },

  // DELETE 请求
  delete: async (url: string, config?: any, retryConfig?: RetryConfig) => {
    return retryRequest(
      () => api.delete(url, config),
      retryConfig || retryConfigs.standard
    );
  },

  // PATCH 请求
  patch: async (url: string, data?: any, config?: any, retryConfig?: RetryConfig) => {
    return retryRequest(
      () => api.patch(url, data, config),
      retryConfig || retryConfigs.standard
    );
  }
};

// 导出原始api实例（保持向后兼容）
export default api;

// 重新导出重试相关的类型和配置
export { retryConfigs } from '../utils/retry';
export type { RetryConfig } from '../utils/retry'; 